import {
  getFieldActions, serializeFieldActions, validateFieldActions,
} from './templateFieldActions';
import { hasFieldValue, matchesFieldCondition } from './fieldConditions';

const equal = (left, right) => JSON.stringify(left) === JSON.stringify(right);
const pathValue = (row, path) => String(path || '').split('.').reduce((value, key) => value?.[key], row);

// One executor per form. Field names are independent of its visible tabs/groups.
export const createFieldActionExecutor = ({ getTemplate, onValue, onOptions, lookup, onError, onState, delayMs = 300 }) => {
  let values = {};
  let observed;
  let disposed = false;
  const writes = new Map();
  const versions = new Map();
  const targetVersions = new Map();
  const timers = new Map();
  const emptyValue = (name) => getTemplate().find((field) => field.fieldname === name)?.multiple ? [] : '';

  const setValue = (name, value, path, initialize = false) => {
    if (disposed || path.has(name) || !getTemplate().some((field) => field.fieldname === name) || equal(values[name], value)) return;
    values = {
      ...values,
      [name]: value,
    };
    writes.set(name, value);
    onValue(name, value);
    if (!initialize) run(name, value, path);
  };

  const run = (name, value, previousPath = new Set(), initialize = false) => {
    if (disposed || previousPath.has(name)) return;
    const field = getTemplate().find((item) => item.fieldname === name);
    if (!field) return;
    const path = new Set([...previousPath, name]);
    const version = (versions.get(name) || 0) + 1;
    versions.set(name, version);
    const actions = serializeFieldActions(getFieldActions(field).filter((action) => action.enabled !== false
      && !Object.keys(validateFieldActions([action], getTemplate().filter((item) => item.fieldname !== name))).length));
    const matchesTrigger = (action) => matchesFieldCondition(value, action.trigger)
      && (!action.trigger.length || String(value ?? '').length === action.trigger.length);
    const activeTargets = new Set(actions.filter(matchesTrigger).flatMap((action) => action.mappings.map((mapping) => mapping.fieldname)));
    let pending = 0;
    let failed = false;
    onState?.(field, {
      loading: false,
    });
    actions.forEach((action, index) => {
      const key = `${name}:${index}`;
      if (timers.has(key)) { clearTimeout(timers.get(key)); timers.delete(key); }
      if (initialize && action.type !== 'lookup') return;
      const matches = matchesTrigger(action);
      const claims = new Map();
      action.mappings.forEach((mapping) => {
        if (!matches && activeTargets.has(mapping.fieldname)) return;
        const next = (targetVersions.get(mapping.fieldname) || 0) + 1;
        targetVersions.set(mapping.fieldname, next);
        claims.set(mapping.fieldname, next);
      });
      const isCurrent = (target) => !disposed && versions.get(name) === version
        && targetVersions.get(target) === claims.get(target);
      if (action.type === 'lookup') {
        action.mappings.forEach((mapping) => {
          if (!matches && activeTargets.has(mapping.fieldname)) return;
          if (mapping.mode === 'options') onOptions(mapping.fieldname, {
            options: [],
            loading: matches,
          });
          if (!initialize && action.resetOnChange) setValue(mapping.fieldname, emptyValue(mapping.fieldname), path);
        });
      }
      if (!matches) return;
      if (action.type !== 'lookup') {
        action.mappings.forEach((mapping) => {
          const next = action.type === 'clear_values' ? emptyValue(mapping.fieldname)
            : mapping.valueFrom === 'source' ? value
              : mapping.valueFrom === 'field' ? values[mapping.sourceFieldname] : mapping.value;
          setValue(mapping.fieldname, next ?? emptyValue(mapping.fieldname), path);
        });
        return;
      }
      pending += 1;
      onState?.(field, {
        loading: true,
      });
      const execute = async () => {
        timers.delete(key);
        try {
          const data = await lookup(action.source, value);
          const rows = Array.isArray(data) ? data : [];
          action.mappings.forEach((mapping) => {
            if (!isCurrent(mapping.fieldname)) return;
            const unique = new Map();
            rows.forEach((row) => {
              const id = pathValue(row, mapping.valuePath);
              if (hasFieldValue(id) && !unique.has(String(id))) unique.set(String(id), {
                id,
                name: String(pathValue(row, mapping.labelPath || mapping.valuePath) ?? id),
              });
            });
            const options = [...unique.values()];
            if (mapping.mode === 'options') onOptions(mapping.fieldname, {
              options,
              loading: false,
            });
            const current = values[mapping.fieldname];
            if (initialize && hasFieldValue(current) && (Array.isArray(current)
              ? current.every((id) => unique.has(String(id))) : unique.has(String(current)))) return;
            if (options.length === 1 && (mapping.mode === 'value' || mapping.autoSelectSingle)) {
              const target = getTemplate().find((item) => item.fieldname === mapping.fieldname);
              setValue(mapping.fieldname, target?.multiple ? [options[0].id] : options[0].id, path, initialize);
            } else if (initialize && action.resetOnChange) {
              setValue(mapping.fieldname, emptyValue(mapping.fieldname), path, true);
            }
          });
        } catch (error) {
          if (disposed || versions.get(name) !== version || !action.mappings.some((mapping) => isCurrent(mapping.fieldname))) return;
          action.mappings.forEach((mapping) => {
            if (isCurrent(mapping.fieldname) && mapping.mode === 'options') onOptions(mapping.fieldname, {
              options: [],
              loading: false,
            });
          });
          failed = true;
          onError?.(field, error);
        } finally {
          if (!disposed && versions.get(name) === version) {
            pending -= 1;
            onState?.(field, {
              loading: pending > 0,
              error: failed,
            });
          }
        }
      };
      if (delayMs) timers.set(key, setTimeout(execute, delayMs));
      else execute();
    });
  };

  return {
    observe(nextValues) {
      if (disposed) return;
      values = {
        ...nextValues,
      };
      const previous = observed;
      observed = {
        ...nextValues,
      };
      const changes = [];
      getTemplate().forEach((field) => {
        const name = field.fieldname;
        if (!name) return;
        if (writes.has(name) && equal(writes.get(name), nextValues[name])) { writes.delete(name); return; }
        writes.delete(name);
        if (previous && !equal(previous[name], nextValues[name])) {
          targetVersions.set(name, (targetVersions.get(name) || 0) + 1);
          if (getFieldActions(field).length) changes.push([name, nextValues[name], false]);
        } else if (!previous && hasFieldValue(nextValues[name]) && getFieldActions(field).length) {
          changes.push([name, nextValues[name], true]);
        }
      });
      changes.forEach(([name, value, initialize]) => run(name, value, new Set(), initialize));
    },
    retry(name) { run(name, values[name], new Set(), true); },
    dispose() {
      disposed = true;
      timers.forEach(clearTimeout);
      timers.clear();
    },
  };
};
